import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { isChecklistMockEnabled } from '@/app/consts/featureFlags';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { DEFAULT_ACTOR } from './_mockRequests';
import {
  type ChecklistActivityRef,
  type ChecklistAnswerChange,
  type ChecklistCheckoutInput,
  type ChecklistRunDetail,
} from './_models';
import {
  cancelActivityRuns,
  getActivityRuns,
  getRun,
  listRuns,
  saveAnswer,
  sendRun,
  skipOptionalRun,
  startRun,
} from './_requests';
import { pendingRunsCount } from './_utils';

export const checklistKeys = {
  all: ['checklists'] as const,
  list: () => [...checklistKeys.all, 'list'] as const,
  detail: (id: string) => [...checklistKeys.all, 'detail', id] as const,
  activity: (taskId: string) => [...checklistKeys.all, 'activity', taskId] as const,
};

const QUERY_OPTIONS = {
  retry: false,
  throwOnError: false,
  meta: { suppressGlobalError: true },
} as const;

const CHECKLIST_LIST_QUERY_OPTIONS = {
  queryKey: checklistKeys.list(),
  queryFn: async () => {
    const response = await listRuns();
    return response.data;
  },
  ...QUERY_OPTIONS,
};

export function useChecklistRuns() {
  return useQuery(CHECKLIST_LIST_QUERY_OPTIONS);
}

export function usePendingChecklistsCount(): number {
  const runsQuery = useQuery({ ...CHECKLIST_LIST_QUERY_OPTIONS, enabled: isChecklistMockEnabled() });
  const runs = runsQuery.data ?? [];
  return pendingRunsCount(runs);
}

export function useChecklistRun(id: string) {
  return useQuery({
    queryKey: checklistKeys.detail(id),
    queryFn: async () => {
      const response = await getRun(id);
      return response.data;
    },
    ...QUERY_OPTIONS,
  });
}

function useChecklistMutationHandlers(id: string) {
  const queryClient = useQueryClient();
  const onSuccess = (detail: ChecklistRunDetail) => {
    queryClient.setQueryData(checklistKeys.detail(id), detail);
    queryClient.invalidateQueries({ queryKey: checklistKeys.list() });
    queryClient.invalidateQueries({ queryKey: [...checklistKeys.all, 'activity'] });
  };
  const onError = (error: Error) => SystemNotification.error(getErrorMessage(error));
  return { onSuccess, onError };
}

export function useChecklistActor(): string {
  const { currentUser } = useAuth();
  return currentUser?.name ?? DEFAULT_ACTOR;
}

export function useStartChecklistRun(id: string) {
  const actor = useChecklistActor();
  const handlers = useChecklistMutationHandlers(id);
  return useMutation({
    mutationFn: async (location: string | undefined) => {
      const response = await startRun(id, location, actor);
      return response.data;
    },
    ...handlers,
  });
}

export function useSaveChecklistAnswer(id: string) {
  const actor = useChecklistActor();
  const handlers = useChecklistMutationHandlers(id);
  return useMutation({
    mutationFn: async (change: ChecklistAnswerChange) => {
      const response = await saveAnswer(id, change, actor);
      return response.data;
    },
    ...handlers,
  });
}

export function useSendChecklistRun(id: string) {
  const actor = useChecklistActor();
  const handlers = useChecklistMutationHandlers(id);
  return useMutation({
    mutationFn: async (input: ChecklistCheckoutInput) => {
      const response = await sendRun(id, input, actor);
      return response.data;
    },
    ...handlers,
  });
}

export function activityChecklistRunsQueryOptions(activity: ChecklistActivityRef | undefined) {
  const taskId = activity?.taskId ?? '';
  return {
    queryKey: checklistKeys.activity(taskId),
    queryFn: async () => {
      if (!activity) throw new Error('Atividade não informada');
      const response = await getActivityRuns(activity);
      return response.data;
    },
    ...QUERY_OPTIONS,
  };
}

export function useActivityChecklistRuns(activity: ChecklistActivityRef | undefined) {
  return useQuery({
    ...activityChecklistRunsQueryOptions(activity),
    enabled: isChecklistMockEnabled() && activity !== undefined,
  });
}

function useActivityMutationHandlers() {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: checklistKeys.list() });
    return queryClient.invalidateQueries({ queryKey: [...checklistKeys.all, 'activity'] });
  };
  const onError = (error: Error) => SystemNotification.error(getErrorMessage(error));
  return { onSuccess, onError };
}

export function useSkipOptionalChecklistRun() {
  const handlers = useActivityMutationHandlers();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await skipOptionalRun(id);
      return response.data;
    },
    ...handlers,
  });
}

export function useCancelActivityChecklistRuns() {
  const handlers = useActivityMutationHandlers();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const response = await cancelActivityRuns(taskId);
      return response.data;
    },
    ...handlers,
  });
}
